import { Component, Input, ChangeDetectorRef, inject } from '@angular/core';
import { LogError, RunView } from '@memberjunction/core';
import { marketingAgentLearningEntity } from 'mj_generatedentities';

type LearningRow = Pick<
    marketingAgentLearningEntity,
    'ID' | 'Finding' | 'Evidence' | 'Source' | 'Confidence' | 'IsPortfolioLevel' | 'DateRecorded' | 'RelatedExperimentID'
>;

@Component({
    standalone: false,
    selector: 'mkt-ws-experiments-learnings',
    template: `
<mkt-ws-page-head
    eyebrow="Experiments · Learnings & anti-patterns"
    title="What we've already learned."
    sub="Searchable findings from completed experiments. Anti-patterns block repeat proposals. Winners graduate to Programs."
    [hasActions]="true">
    <div actions>
        <button class="ln__btn" type="button" [class.ln__btn--active]="filterPortfolio" (click)="filterPortfolio = !filterPortfolio">
            <i class="fa-solid fa-share-nodes"></i> Cross-company
        </button>
    </div>
</mkt-ws-page-head>

<div *ngIf="loading" class="ln__loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading learnings…</div>

<ng-container *ngIf="!loading">
    <div class="ln__totals">
        <mkt-ws-card><div class="ln__caps">Total findings</div><div class="ln__big">{{ all.length }}</div><div class="ln__sub">Across {{ uniqueSources }} source types</div></mkt-ws-card>
        <mkt-ws-card><div class="ln__caps">Anti-patterns</div><div class="ln__big">{{ antiPatternCount }}</div><div class="ln__sub">Block new proposals</div></mkt-ws-card>
        <mkt-ws-card><div class="ln__caps">Graduated · Evergreen</div><div class="ln__big">{{ graduatedCount }}</div><div class="ln__sub">Now live in Programs</div></mkt-ws-card>
        <mkt-ws-card><div class="ln__caps">High-confidence</div><div class="ln__big">{{ highConfCount }}</div><div class="ln__sub">Strong signal · actionable</div></mkt-ws-card>
    </div>

    <mkt-ws-card class="ln__filters">
        <div style="display:flex; align-items:center; gap:8px; flex-wrap: wrap;">
            <input type="text" class="ln__search" [(ngModel)]="search" placeholder="Search findings…" />
            <select class="ln__filter" [(ngModel)]="filterSource">
                <option value="">All sources</option>
                <option *ngFor="let s of sources" [value]="s">{{ s }}</option>
            </select>
            <select class="ln__filter" [(ngModel)]="filterConfidence">
                <option value="">All confidence</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
            </select>
            <span style="margin-left:auto; font-size: 11.5px; color: var(--mj-text-secondary, #71717a);">Showing {{ filtered.length }} of {{ all.length }}</span>
        </div>
    </mkt-ws-card>

    <mkt-ws-card [hasHead]="true" [noPadding]="true">
        <div head><span class="ln__caps-ink">Findings</span></div>
        <div *ngIf="filtered.length === 0" class="ln__empty">No findings match the filters. Adjust the search or run more experiments — anti-patterns and positive findings populate as the agent learns.</div>
        <button *ngFor="let f of filtered" type="button" class="ln__row" (click)="open(f.ID)">
            <div class="ln__row-head">
                <mkt-ws-pill [variant]="confVariant(f.Confidence)">{{ f.Confidence || '—' }}</mkt-ws-pill>
                <mkt-ws-pill *ngIf="isAntiPattern(f)" variant="bad"><i class="fa-solid fa-shield-halved"></i> Anti-pattern</mkt-ws-pill>
                <mkt-ws-pill *ngIf="!isAntiPattern(f)" variant="good"><i class="fa-solid fa-sparkles"></i> Positive</mkt-ws-pill>
                <mkt-ws-pill variant="muted">{{ f.Source || '—' }}</mkt-ws-pill>
                <mkt-ws-pill *ngIf="f.IsPortfolioLevel" variant="info">Portfolio</mkt-ws-pill>
                <span class="ln__ts">{{ formatDate(f.DateRecorded) }}</span>
            </div>
            <div class="ln__row-title">{{ f.Finding }}</div>
            <div class="ln__row-body" *ngIf="f.Evidence">{{ truncate(f.Evidence, 220) }}</div>
        </button>
    </mkt-ws-card>
</ng-container>
    `,
    styles: [`
        :host { display: block; }
        .ln__loading { padding: 60px 20px; text-align: center; color: var(--mj-text-secondary, #71717a); }
        .ln__btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; height: 30px; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); border-radius: 6px; font: inherit; font-size: 12px; cursor: pointer; color: var(--mj-text-primary, #1a1a1a); }
        .ln__btn:hover { background: var(--mj-bg-page, #f5f5f5); }
        .ln__btn--active { background: var(--mj-text-primary, #1a1a1a); color: var(--mj-bg-surface, #fff); border-color: var(--mj-text-primary, #1a1a1a); }

        .ln__caps, .ln__caps-ink { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); }
        .ln__caps-ink { color: var(--mj-text-primary, #1a1a1a); }
        .ln__sub { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); }
        .ln__big { font-family: ui-monospace, monospace; font-variant-numeric: tabular-nums; font-size: 22px; color: var(--mj-text-primary, #1a1a1a); margin: 4px 0 2px; }

        .ln__totals { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
        .ln__filters { margin-bottom: 16px; }

        .ln__search { padding: 6px 10px; height: 32px; font: inherit; font-size: 13px; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; background: var(--mj-bg-surface, #fff); color: var(--mj-text-primary, #1a1a1a); width: 240px; }
        .ln__filter { padding: 6px 10px; height: 32px; font: inherit; font-size: 12px; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; background: var(--mj-bg-surface, #fff); color: var(--mj-text-primary, #1a1a1a); }

        .ln__empty { padding: 32px; text-align: center; font-size: 12.5px; color: var(--mj-text-secondary, #71717a); }
        .ln__row { display: block; padding: 18px 20px; border: none; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); width: 100%; text-align: left; cursor: pointer; background: transparent; font: inherit; transition: background 0.15s; }
        .ln__row:last-child { border-bottom: none; }
        .ln__row:hover { background: var(--mj-bg-page, #f5f5f5); }
        .ln__row-head { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-bottom: 6px; }
        .ln__ts { margin-left: auto; font-size: 11px; color: var(--mj-text-secondary, #71717a); font-family: ui-monospace, monospace; }
        .ln__row-title { font-size: 14px; font-weight: 500; color: var(--mj-text-primary, #1a1a1a); margin-bottom: 4px; line-height: 1.4; }
        .ln__row-body { font-size: 12.5px; color: var(--mj-text-secondary, #71717a); line-height: 1.5; }

        @media (max-width: 1024px) {
            .ln__totals { grid-template-columns: repeat(2, 1fr); }
        }
    `]
})
export class ExperimentsLearningsSection {
    private _companyId = '';
    @Input() set companyId(v: string) {
        if (v && v !== this._companyId) {
            this._companyId = v;
            void this.load();
        } else if (!v) {
            this._companyId = '';
            this.all = [];
            this.cdr.detectChanges();
        }
    }
    get companyId(): string { return this._companyId; }

    @Input() navigate: (path: string) => void = () => {};
    @Input() navigateWithId: (path: string, id?: string) => void = () => {};

    private cdr = inject(ChangeDetectorRef);

    loading = false;
    all: LearningRow[] = [];

    search = '';
    filterSource = '';
    filterConfidence: '' | 'High' | 'Medium' | 'Low' = '';
    filterPortfolio = false;

    get sources(): string[] {
        return Array.from(new Set(this.all.map(f => f.Source).filter((s): s is 'analysis' | 'experiment' | 'observation' => !!s)));
    }
    get uniqueSources(): number {
        return this.sources.length;
    }
    get antiPatternCount(): number {
        return this.all.filter(f => this.isAntiPattern(f)).length;
    }
    get graduatedCount(): number {
        return this.all.filter(f => /graduate|evergreen/i.test(f.Finding || '')).length;
    }
    get highConfCount(): number {
        return this.all.filter(f => f.Confidence === 'High').length;
    }
    get filtered(): LearningRow[] {
        const q = this.search.trim().toLowerCase();
        return this.all.filter(f => {
            if (this.filterSource && f.Source !== this.filterSource) return false;
            if (this.filterConfidence && f.Confidence !== this.filterConfidence) return false;
            if (this.filterPortfolio && !f.IsPortfolioLevel) return false;
            if (!q) return true;
            return (f.Finding || '').toLowerCase().includes(q)
                || (f.Evidence || '').toLowerCase().includes(q);
        });
    }

    isAntiPattern(f: LearningRow): boolean {
        return /anti.?pattern|don.?t|do not|fail/i.test(f.Finding || '');
    }
    confVariant(c: string | undefined | null): 'good' | 'warn' | 'bad' | 'muted' {
        switch (c) {
            case 'High': return 'good';
            case 'Medium': return 'warn';
            case 'Low': return 'bad';
            default: return 'muted';
        }
    }
    formatDate(d: Date | string | null | undefined): string {
        if (!d) return '—';
        return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
    truncate(s: string, n: number): string {
        return s.length > n ? s.slice(0, n - 1) + '…' : s;
    }

    open(id: string): void {
        this.navigateWithId('experiments/learnings/detail', id);
    }

    private async load(): Promise<void> {
        if (!this._companyId) return;
        const cid = this._companyId;
        this.loading = true;
        this.cdr.detectChanges();
        try {
            const rv = new RunView();
            const r = await rv.RunView<LearningRow>({
                EntityName: 'Agent Learnings',
                ExtraFilter: `CompanyID='${cid}' OR IsPortfolioLevel=1`,
                OrderBy: 'DateRecorded DESC',
                MaxRows: 500,
                ResultType: 'simple',
            });
            if (r.Success) {
                this.all = r.Results || [];
            } else if (r.ErrorMessage) {
                LogError(`[ExperimentsLearnings] query failed: ${r.ErrorMessage}`);
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[ExperimentsLearnings] load failed: ${msg}`);
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }
}
